/**
  Copyright Highway9 Networks Inc.
 */
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Clock, ClockPool, SynchronizationProfile } from "~/types/SyncProfile";
import { RootState } from "..";
import { clockPoolService, clockService } from "~/services/clockpool-service";

type LoadingState = "idle" | "loading" | "succeeded" | "failed";

type initState = {
  // Legacy sync profiles
  open: boolean;
  current: SynchronizationProfile | null;
  data: SynchronizationProfile[];

  // Clock Pools
  clockPools: ClockPool[];
  clockPoolsLoading: LoadingState;
  clockPoolsError: string | null;

  // Clocks
  clocks: Clock[];
  clocksLoading: LoadingState;
  clocksError: string | null;
};

const initialState: initState = {
  // Legacy
  open: false,
  current: null,
  data: [],

  // Clock Pools
  clockPools: [],
  clockPoolsLoading: "idle",
  clockPoolsError: null,

  // Clocks
  clocks: [],
  clocksLoading: "idle",
  clocksError: null,
};

// Clock Pool thunks (fetch only - create/update/delete are called directly from components)
export const fetchClockPools = createAsyncThunk(
  "syncProfile/fetchClockPools",
  async (_, { rejectWithValue }) => {
    try {
      const data = await clockPoolService.getAll();
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch clock pools");
    }
  }
);

// Clock thunks (fetch only - create/update/delete are called directly from components)
export const fetchClocks = createAsyncThunk(
  "syncProfile/fetchClocks",
  async (_, { rejectWithValue }) => {
    try {
      const data = await clockService.getAll();
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch clocks");
    }
  }
);

const syncProfileSlice = createSlice({
  name: "syncProfile",
  initialState,
  reducers: {
    // Legacy reducers
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setValues: (state, action: PayloadAction<SynchronizationProfile | null>) => {
      state.current = action.payload;
    },
    updateData: (state, action: PayloadAction<SynchronizationProfile>) => {
      const index = state.data.findIndex((item) => item.id === action.payload.id);
      state.data[index] = action.payload;
    },
    delete: (state, action: PayloadAction<string>) => {
      const index = state.data.findIndex((item) => item.id === action.payload);
      state.data = state.data.filter((_, i) => i !== index);
    },
    setData: (state, action: PayloadAction<SynchronizationProfile[]>) => {
      state.data = action.payload;

      const current = action.payload.find((item) => item.id === state.current?.id);
      if (current) {
        state.current = current;
      }
    },
    addData: (state, action: PayloadAction<SynchronizationProfile>) => {
      state.data.push(action.payload);
    },

    // Clock Pool reducers
    setClockPools: (state, action: PayloadAction<ClockPool[]>) => {
      state.clockPools = action.payload;
    },
    clearClockPoolsError: (state) => {
      state.clockPoolsError = null;
    },

    // Clock reducers
    setClocks: (state, action: PayloadAction<Clock[]>) => {
      state.clocks = action.payload;
    },
    clearClocksError: (state) => {
      state.clocksError = null;
    },
  },
  extraReducers: (builder) => {
    // Clock Pools
    builder
      .addCase(fetchClockPools.pending, (state) => {
        state.clockPoolsLoading = "loading";
        state.clockPoolsError = null;
      })
      .addCase(fetchClockPools.fulfilled, (state, action) => {
        state.clockPoolsLoading = "succeeded";
        state.clockPools = action.payload;
      })
      .addCase(fetchClockPools.rejected, (state, action) => {
        state.clockPoolsLoading = "failed";
        state.clockPoolsError = action.payload as string;
      });

    // Clocks
    builder
      .addCase(fetchClocks.pending, (state) => {
        state.clocksLoading = "loading";
        state.clocksError = null;
      })
      .addCase(fetchClocks.fulfilled, (state, action) => {
        state.clocksLoading = "succeeded";
        state.clocks = action.payload;
      })
      .addCase(fetchClocks.rejected, (state, action) => {
        state.clocksLoading = "failed";
        state.clocksError = action.payload as string;
      });
  },
});

export const syncProfileActions = syncProfileSlice.actions;
export default syncProfileSlice.reducer;

// Legacy selectors
export const syncProfileState = (state: RootState) => state.syncProfile.current;
export const syncProfileData = (state: RootState) => state.syncProfile.data;
export const syncProfileOpen = (state: RootState) => state.syncProfile.open;

// Clock Pool selectors
export const clockPoolData = (state: RootState) => state.syncProfile.clockPools;
export const clockPoolLoading = (state: RootState) => state.syncProfile.clockPoolsLoading;
export const clockPoolError = (state: RootState) => state.syncProfile.clockPoolsError;

// Clock selectors
export const clockData = (state: RootState) => state.syncProfile.clocks;
export const clockLoading = (state: RootState) => state.syncProfile.clocksLoading;
export const clockError = (state: RootState) => state.syncProfile.clocksError;

// Derived selectors
export const clocksByPoolId = (poolId: string) => (state: RootState) =>
  state.syncProfile.clocks.filter((clock) => clock.clockPool?.id === poolId);
